import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';

/**
 * Shared single-spinner scope for tabbed detail screens.
 *
 * Detail screens (Bid, Vendor, Customer, Job, …) stack many independent widgets
 * inside one tab, and each widget renders its own <mat-spinner>. Opening a tab
 * would otherwise flash three or four spinners at once in different places.
 *
 * Wrap the screen's <mat-tab-group> in this component and bind `ready` to the
 * screen's MAIN record. Until that record has arrived the tab body is hidden and
 * this component shows one centered spinner; the moment it arrives the body is
 * revealed, and every secondary widget (stats, related lists, documents,
 * activity, …) keeps loading in place behind its own inline spinner.
 *
 *   <app-tab-loading-scope [ready]="!!model">
 *     <mat-tab-group>…</mat-tab-group>
 *   </app-tab-loading-scope>
 *
 * `ready` is the whole contract: MAIN RECORD AVAILABLE = SPINNER OFF. Nothing
 * else may hold the page back.
 *
 * This deliberately replaces an earlier MutationObserver implementation that
 * inferred "loading" from any <mat-spinner> living anywhere in the tab body.
 * That version kept the entire page hidden until the SLOWEST widget on the
 * screen finished — on Bid detail the record arrived at ~3.6s but the document
 * widgets only settled at ~9.3s, so the user stared at a spinner for a further
 * 5.7 seconds with the real data already in hand. Gating on the main record
 * removes that class of delay for every screen at once, and cannot regress: the
 * scope now waits on exactly one request and never observes the DOM.
 *
 * Left unbound, `ready` defaults to true and the scope never blocks — a screen
 * that forgets to bind it shows its widgets' own spinners rather than a stuck
 * overlay.
 *
 * Deliberately visibility-based, never display:none — collapsing the body would
 * change the layout the widgets measure themselves against while they load.
 */
@Component({
  selector: 'app-tab-loading-scope',
  templateUrl: './tab-loading-scope.component.html',
  styleUrls: ['./tab-loading-scope.component.scss'],
  standalone: false,
})
export class TabLoadingScopeComponent implements OnChanges, OnDestroy {
  /**
   * The screen's main record (or any truthy "the record is here" flag).
   *
   * Falsy → the record has not arrived, show the scope spinner.
   * Truthy → reveal the page immediately; secondary data keeps loading.
   */
  @Input() ready: unknown = true;

  /** Text under the spinner. Set to '' to show the spinner on its own. */
  @Input() label = 'Loading...';

  /** Spinner size, matching the `diameter="48"` the detail screens already used. */
  @Input() diameter = 48;

  /**
   * Safety valve. A main record request that errors out (or never returns) would
   * otherwise leave `ready` false and the body hidden forever, so the scope
   * releases after this and lets the screen render its own empty/error state.
   */
  @Input() maxDurationMs = 20000;

  /** Set to false to leave a screen on its widget-level spinners. */
  @Input() enabled = true;

  isLoading = false;

  /** Set once the safety valve fires, so releasing cannot oscillate back on. */
  private timedOut = false;
  private releaseTimer: any = null;

  ngOnChanges(): void {
    this.sync();
  }

  ngOnDestroy(): void {
    clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
  }

  private sync(): void {
    // A record that arrives late still counts: clear the valve so a subsequent
    // reload of the same screen can show the spinner again.
    if (this.ready) {
      this.timedOut = false;
    }

    const loading = this.enabled && !this.ready && !this.timedOut;
    if (loading === this.isLoading) {
      return;
    }

    this.isLoading = loading;
    clearTimeout(this.releaseTimer);
    this.releaseTimer = null;

    if (loading) {
      this.releaseTimer = setTimeout(() => this.release(), this.maxDurationMs);
    }
  }

  private release(): void {
    this.timedOut = true;
    this.isLoading = false;
  }
}
